import React, { useCallback } from 'react';
import { Label, Radio, Tooltip, Box } from '@nimbus-ds/components';
import { InfoCircleIcon } from '@nimbus-ds/icons';
import { LimitScopeTypes } from '@tiendanube/common/src/domains/marketing/promotions/LimitScopeTypes';
import { Stack } from '@tiendanube/components';
import { useTranslationMarketing } from 'domains/Marketing/hook';
import { PaymentOptionSelector } from './PaymentOptionSelector';
import { PromotionFormProps } from '../../Promotions/components';

type PaymentOptionsProps = Pick<
  PromotionFormProps,
  'values' | 'errors' | 'onChange' | 'onChangePaymentOptions'
> & {
  shouldLimitToNuvemPago: boolean;
};

export function PaymentOptions({
  values,
  errors,
  onChange,
  onChangePaymentOptions,
  shouldLimitToNuvemPago,
}: Readonly<PaymentOptionsProps>) {
  const t = useTranslationMarketing(
    'promotions.settingsPage.limits.paymentOptions',
  );
  const requiredError = errors['paymentOptions'] ? t('required') : '';

  const { paymentOptionsScopeType, paymentOptions } = values;

  const handleScopeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      onChange(e);

      if (value === LimitScopeTypes.UNLIMITED) {
        onChangePaymentOptions([]);
      }
    },
    [onChange, onChangePaymentOptions],
  );

  return (
    <>
      <Stack spacing="tight">
        <Radio
          id="paymentOptionsScopeTypeUnlimited"
          name="paymentOptionsScopeType"
          as="button"
          value={LimitScopeTypes.UNLIMITED}
          checked={paymentOptionsScopeType === LimitScopeTypes.UNLIMITED}
          label={t('scope.unlimited')}
          onChange={handleScopeChange}
        />
        <Box display="flex" alignItems="center" gap="2">
          <Radio
            id="paymentOptionsScopeTypeLimited"
            name="paymentOptionsScopeType"
            as="button"
            value={LimitScopeTypes.LIMITED}
            checked={paymentOptionsScopeType === LimitScopeTypes.LIMITED}
            label={t('scope.limited')}
            onChange={handleScopeChange}
          />
          {shouldLimitToNuvemPago && (
            <Tooltip content={t('modal.nuvemPagoLimitWarning')} arrow>
              <InfoCircleIcon size="small" />
            </Tooltip>
          )}
        </Box>
      </Stack>

      {paymentOptions &&
        paymentOptionsScopeType === LimitScopeTypes.LIMITED && (
          <>
            <Label>
              {paymentOptions.length > 0
                ? t('scope.limitedInfoWithSelection')
                : t('scope.limitedInfoWithoutSelection')}
            </Label>

            <PaymentOptionSelector
              paymentOptions={paymentOptions}
              onChange={onChangePaymentOptions}
              requiredText={requiredError}
              shouldLimitToNuvemPago={shouldLimitToNuvemPago}
            />
          </>
        )}
    </>
  );
}
